import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2 } from "lucide-react";
import { useAuth } from "../../lib/auth/hooks";
import { usePermission } from "../../lib/auth/hooks";
import { hasRoleInPayload } from "../../lib/auth/permissions";
import { Text } from "../../../ui/components/Text";
import { Callout } from "../../../ui/components/Callout";
import { ListView } from "../../../ui/components/ListView";
import type { ListViewCTAGroup } from "../../../ui/components/ListView/components/ListViewCard";
import * as adminUsersApi from "../../lib/api/admin-users";
import type { AdminUserListItem } from "../../lib/api/admin-users";
import {
  approveOrganizationMembershipRequest,
  denyOrganizationMembershipRequest,
} from "../../lib/api/users";
import { authenticatedProApiFetch } from "../../lib/api/pro-api.client";
import type { ListParams } from "../../../ui/api/listApi.types";
import {
  buildUsersTableColumns,
  getUserRowAccent,
} from "./usersTableColumns";
import { ListViewUserCard } from "../../../ui/components/ListView/components/ListViewUserCard";
import { buildUserSelectionActions } from "./usersBulkActions";
import { useListImportExport } from "../data-transfer/useListImportExport";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import { useWebSocket } from "../../hooks/useWebSocket";
import {
  orgContextHash,
  useOrganizationContextOptional,
} from "../../lib/organization-context";
import { useI18n } from "../../lib/i18n";
import * as pageStyles from "./UsersListPage.css";

function resolveEffectiveOrgIds(
  params: ListParams,
  isSuperAdmin: boolean,
  contextOrgIds: string[],
): string[] | undefined {
  const tableFilter = params.filters?.organization_ids;
  const filterIds = Array.isArray(tableFilter)
    ? tableFilter.map(String).filter(Boolean)
    : [];

  if (isSuperAdmin) {
    return filterIds.length > 0 ? filterIds : undefined;
  }

  if (contextOrgIds.length === 0) {
    return filterIds.length > 0 ? filterIds : undefined;
  }

  if (filterIds.length > 0) {
    return filterIds.filter((id) => contextOrgIds.includes(id));
  }

  return contextOrgIds;
}

export function UsersListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const { accessToken, user, userPayload } = useAuth();
  const orgContext = useOrganizationContextOptional();
  const { hasPermissionSync: canDelete } = usePermission("user:delete");
  const { hasPermissionSync: canHardDelete } = usePermission("user:hard-delete");

  const isSuperAdmin = hasRoleInPayload(userPayload, "super-admin");
  const selectedOrgIds = orgContext?.selectedOrgIds ?? [];
  const contextKey = orgContext ? orgContextHash(selectedOrgIds) : "none";

  const token =
    accessToken ??
    (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);

  const { data: allOrgsData } = useQuery({
    queryKey: ["organizations", "all-for-filter"],
    queryFn: () =>
      authenticatedProApiFetch<{ organizations: Array<{ id: string; name: string }> }>(
        "/organizations/all",
        token!,
        { method: "GET" },
      ),
    enabled: Boolean(token && isSuperAdmin),
    staleTime: 60_000,
  });

  const orgFilterOptions = React.useMemo(() => {
    if (isSuperAdmin && allOrgsData?.organizations?.length) {
      return allOrgsData.organizations.map((org) => ({
        value: org.id,
        label: org.name,
      }));
    }
    if (orgContext?.hasOrganizationMembership) {
      return orgContext.availableOrgs
        .filter((org) => selectedOrgIds.includes(org.id))
        .map((org) => ({ value: org.id, label: org.name }));
    }
    return [];
  }, [isSuperAdmin, allOrgsData, orgContext, selectedOrgIds]);

  const columns = React.useMemo(
    () => buildUsersTableColumns(orgFilterOptions),
    [orgFilterOptions],
  );

  const initialColumnFilters = React.useMemo(
    () =>
      selectedOrgIds.length > 0
        ? [{ id: "organizations", value: selectedOrgIds }]
        : [],
    [selectedOrgIds.join(",")],
  );

  const visibleRowsRef = React.useRef<AdminUserListItem[]>([]);

  const { subscribe, unsubscribe } = useWebSocket();

  React.useEffect(() => {
    const handler = () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users', 'list'] });
    };
    subscribe(['entity:users:*'], handler);
    return () => unsubscribe(['entity:users:*'], handler);
  }, [subscribe, unsubscribe, queryClient]);

  const invalidateUsersList = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["admin-users", "list"] });
  }, [queryClient]);

  const { confirm, ConfirmModal } = useConfirmModal();

  const [listTotal, setListTotal] = React.useState(0);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [exportPreviewItems, setExportPreviewItems] = React.useState<
    Array<{ id: string; label: string; matchKey?: string }>
  >([]);

  const selectionActions = React.useMemo(() => {
    if (!token) return undefined;
    const actions = buildUserSelectionActions({
      accessToken: token,
      canDelete: canDelete(),
      canHardDelete: canHardDelete(),
      confirm,
      getVisibleRows: () => visibleRowsRef.current,
      onMutationComplete: invalidateUsersList,
    });
    return actions.length > 0 ? actions : undefined;
  }, [token, canDelete, canHardDelete, confirm, invalidateUsersList]);

  const {
    headerActions: importExportHeaderActions,
    exportContextAction,
    modals: importExportModals,
  } = useListImportExport({
    entity: "users",
    entityLabel: "Users",
    enableImport: true,
    enableExport: true,
    totalCount: listTotal,
    selectedIds,
    previewItems: exportPreviewItems,
    defaultFormat: "csv",
    onImported: invalidateUsersList,
  });

  const selectionActionsWithExport = React.useMemo(() => {
    const base = selectionActions ?? [];
    if (!exportContextAction) return selectionActions;
    return [
      ...base,
      {
        icon: exportContextAction.icon,
        label: exportContextAction.label,
        onClick: (ids: string[]) => exportContextAction.onClick(ids),
      },
    ];
  }, [selectionActions, exportContextAction]);

  const buildMembershipCtas = React.useCallback(
    (row: AdminUserListItem): ListViewCTAGroup[] | undefined => {
      const orgs = row.pendingMembershipOrgs ?? [];
      if (!orgs.length || !token) return undefined;
      const realGroups = orgs.map((org) => ({
        id: org.id,
        text: "Membership requested",
        image: {
          src: org.logoUrl,
          tooltip: org.name,
          onClick: () => navigate({ to: "/workspace/organizations/$orgId", params: { orgId: org.id } }),
        },
        buttons: [
          {
            id: "approve",
            label: "Approve",
            variant: "primary" as const,
            onClick: () => {
              void approveOrganizationMembershipRequest(token, org.id, row.id).then(
                invalidateUsersList,
              );
            },
          },
          {
            id: "deny",
            label: "Deny",
            variant: "secondary" as const,
            onClick: () => {
              void denyOrganizationMembershipRequest(token, org.id, row.id).then(
                invalidateUsersList,
              );
            },
          },
        ],
      }));
      return realGroups.length > 0 ? realGroups : undefined;
    },
    [token, invalidateUsersList, navigate],
  );

  const fetchUsers = React.useCallback(
    async (params: ListParams) => {
      if (!token) {
        return { data: [], total: 0 };
      }

      const effectiveOrgIds = resolveEffectiveOrgIds(
        params,
        isSuperAdmin,
        selectedOrgIds,
      );

      const nextParams: ListParams = { ...params };
      const filters = { ...(params.filters ?? {}) };
      if (effectiveOrgIds?.length) {
        filters.organization_ids = effectiveOrgIds;
      } else {
        delete filters.organization_ids;
      }
      nextParams.filters = Object.keys(filters).length ? filters : undefined;

      const result = await adminUsersApi.fetchUsersList(token, nextParams);
      visibleRowsRef.current = result.data;
      setExportPreviewItems(
        result.data.map((u) => ({
          id: u.id,
          label:
            [u.firstName, u.lastName].filter(Boolean).join(" ").trim() || u.email,
          matchKey: u.email,
        })),
      );
      setListTotal(result.total);
      return result;
    },
    [token, isSuperAdmin, selectedOrgIds.join(",")],
  );

  const showContextHint =
    orgContext != null &&
    orgContext.hasOrganizationMembership &&
    orgContext.availableOrgs.length > orgContext.selectedOrgIds.length;

  const tableFooter = showContextHint ? (
    <div className={pageStyles.contextHintBanner} role="status">
      <Building2
        className={pageStyles.contextHintIcon}
        strokeWidth={1.75}
        size={14}
        aria-hidden
      />
      <span>
        Some of your organizations are hidden by the current context. Change
        context in the navigation bar to include them.
      </span>
    </div>
  ) : undefined;

  if (!token) {
    return (
      <div className={pageStyles.page}>
        <Callout variant="error">{t("pro.users.signInRequired")}</Callout>
      </div>
    );
  }

  return (
    <div className={pageStyles.page}>
      <div className={pageStyles.pageHeader}>
        <Text level="h1" color="text" weight="bold">
          Users
        </Text>
      </div>

      <div className={pageStyles.tableHost}>
        <ListView<AdminUserListItem>
          listId="admin-users"
          userId={user?.id ?? "anonymous"}
          columns={columns}
          fetchList={fetchUsers}
          contextKey={contextKey}
          initialColumnFilters={initialColumnFilters}
          footer={tableFooter}
          fillContainer
          allowFullscreen
          enableSavedViews
          resourceType="users"
          enabledViews={["table", "deck", "stack"]}
          enableSelection={Boolean(selectionActionsWithExport?.length)}
          selectionActions={selectionActionsWithExport}
          onSelectionChange={setSelectedIds}
          headerActions={importExportHeaderActions}
          getRowLabel={row => [row.firstName, row.lastName].filter(Boolean).join(' ').trim() || row.email}
          searchPlaceholder="Search users by name, email, or phone…"
          emptyTitle="No users found"
          emptySubtitle={t("pro.users.emptySubtitle")}
          emptyIcon="users"
          defaultSort={{ sort_by: "name", order: "asc" }}
          getRowAccent={getUserRowAccent}
          getRowPinned={(row) => (row.pendingMembershipOrgs?.length ?? 0) > 0}
          getRowCtas={buildMembershipCtas}
          onRowClick={(row) =>
            navigate({ to: "/workspace/users/$userId", params: { userId: row.id } })
          }
          renderCard={(row, ctx) => (
            <ListViewUserCard
              key={row.id}
              layout="deck"
              user={row}
              onClick={ctx.onClick}
            />
          )}
          renderStackCard={(row, ctx) => (
            <ListViewUserCard
              key={row.id}
              layout="stack"
              user={row}
              onClick={ctx.onClick}
            />
          )}
        />
      </div>
      <ConfirmModal />
      {importExportModals}
    </div>
  );
}
